import { useState, useRef, useCallback, useEffect } from "react";
import { motion, useMotionValue, animate } from "framer-motion";

// ─── Divider / Handle ────────────────────────────────────────────────────────
function Divider({ direction, onDrag, onDoubleClick, dividerColor='' }) {
  const isHorizontal = direction === "horizontal";
  const [active, setActive] = useState(false);
  const [hovering, setHovering] = useState(false);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      setActive(true);

      const startPos = isHorizontal ? e.clientX : e.clientY;
      onDrag({ type: "start", pos: startPos });

      const onMouseMove = (ev) => {
        const pos = isHorizontal ? ev.clientX : ev.clientY;
        onDrag({ type: "move", pos });
      };

      const onMouseUp = () => {
        setActive(false);
        onDrag({ type: "end" });
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [isHorizontal, onDrag]
  );

  const handleTouchStart = useCallback(
    (e) => {
      const touch = e.touches[0];
      const startPos = isHorizontal ? touch.clientX : touch.clientY;
      setActive(true);
      onDrag({ type: "start", pos: startPos });

      const onTouchMove = (ev) => {
        const t = ev.touches[0];
        const pos = isHorizontal ? t.clientX : t.clientY;
        onDrag({ type: "move", pos });
      };

      const onTouchEnd = () => {
        setActive(false);
        onDrag({ type: "end" });
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("touchend", onTouchEnd);
      };

      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd);
    },
    [isHorizontal, onDrag]
  );

  return (
    <motion.div
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onDoubleClick={onDoubleClick}
      className={[
        "relative flex items-center justify-center flex-shrink-0 select-none z-10",
        isHorizontal
          ? "w-[6px] cursor-col-resize h-full"
          : "h-[6px] cursor-row-resize w-full",
      ].join(" ")}
    //   style={{
    //     background: active
    //       ? dividerColor || "var(--borderSlateGrey)"
    //       : hovering
    //       ? 'var(--primary-color)'
    //       : "transparent",
    //     transition: "background 0.15s ease",
    //   }}
    >
      {/* Track line */}
      <motion.div
        className={[
          "rounded-full",
          isHorizontal ? "w-[2px] h-full" : "h-[2px] w-full",
        ].join(" ")}
        animate={{
          background: active
            ? "var(--primary-color)"
            : hovering
            ? "var(--primary-color)"
            : dividerColor || "var(--borderSlateGrey)",
        //   scaleX: isHorizontal ? (active ? 1.5 : 1) : 1,
        //   scaleY: isHorizontal ? 1 : active ? 1.5 : 1,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      />

      {/* Grip dots */}
      {/* <motion.div
        className={[
          "absolute flex gap-[3px] top-[1px]",
          isHorizontal ? "flex-col" : "flex-row",
        ].join(" ")}
        animate={{ opacity: hovering || active ? 1 : 0.4 }}
        transition={{ duration: 0.15 }}
      > */}
        {/* {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-[3px] h-[3px] rounded-full"
            animate={{
              background: active ? "#6366f1" : "#94a3b8",
              scale: active ? 1.2 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 20,
              delay: i * 0.03,
            }}
          />
        ))} */}
        <motion.div
            className={`absolute rounded-full border-[1px] shadow transition duration-200 group-hover:delay-75 ${isHorizontal ? 'w-[8px] h-[24px] cursor-col-resize' : 'w-[24px] h-[8px] cursor-row-resize'} ${(hovering || active) ? 'border-primary' : 'border-borderSlateGrey'}`}
            animate={{
              background: (active || hovering) ? "var(--primary-color)" : "white",
            //   scale: active ? 1.2 : 1,
            }}
            transition={{
            //   type: "spring",
            //   stiffness: 400,
            //   damping: 20,
            //   delay: i * 0.03,
            duration: 0.2,
            }}
            
        ></motion.div>
      {/* </motion.div> */}
    </motion.div>
  );
}

// ─── SplitPane ────────────────────────────────────────────────────────────────
export default function SplitPane({
  direction = "horizontal",
  initialSplit = 50,
  minSize = 80,
  children,
  className = "",
  dividerColor=''
}) {
  const isHorizontal = direction === "horizontal";
  const containerRef = useRef(null);
  const [split, setSplit] = useState(initialSplit); // percent 0–100
  const lastSplit = useRef(split);

  const clamp = useCallback(
    (pct) => {
      const container = containerRef.current;
      if (!container) return pct;
      const total = isHorizontal
        ? container.offsetWidth
        : container.offsetHeight;
      const minPct = (minSize / total) * 150;
      const maxPct = 150 - minPct;
      return Math.min(maxPct, Math.max(minPct, pct));
    },
    [isHorizontal, minSize]
  );

  const dragStartSplit = useRef(initialSplit);
  const dragStartPos = useRef(0);

  const handleDrag = useCallback(
    ({ type, pos }) => {
      if (type === "start") {
        dragStartSplit.current = split;
        dragStartPos.current = pos;
        return;
      }
      if (type === "end") return;

      const container = containerRef.current;
      if (!container) return;
      const total = isHorizontal
        ? container.offsetWidth
        : container.offsetHeight;
      const deltaPct = ((pos - dragStartPos.current) / total) * 100;
      const next = clamp(dragStartSplit.current + deltaPct);
      lastSplit.current = next;
      setSplit(next);
    },
    [isHorizontal, clamp, split]
  );

  const handleDoubleClick = useCallback(() => {
    setSplit((prev) => {
      const target = Math.abs(prev - 50) < 2 ? lastSplit.current : 50;
      return clamp(target);
    });
  }, [clamp]);

  const [a, b] = Array.isArray(children) ? children : [children, null];

  const paneStyle = (size) =>
    isHorizontal ? { width: `${size}%` } : { height: `${size}%` };

  return (
    <div
      ref={containerRef}
      className={[
        "flex overflow-hidden",
        isHorizontal ? "flex-row" : "flex-col",
        className,
      ].join(" ")}
    >
      {a && <motion.div
        className="overflow-hidden"
        style={paneStyle(split)}
        layout={false}
      >
        {a}
      </motion.div>}

      {(a && b) && <Divider
        direction={direction}
        onDrag={handleDrag}
        onDoubleClick={handleDoubleClick}
        dividerColor={dividerColor}
      />}

      {b && <motion.div className="overflow-hidden flex-1 min-h-[150px]" layout={false}>
        {b}
      </motion.div>}
    </div>
  );
}

// ─── Demo ─────────────────────────────────────────────────────────────────────
// export const Panel = ({ label, color, children }) => (
//   <div className="flex flex-col gap-3">
//     {children}
//   </div>
// );
